import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Mail,
  Bell,
  ShieldCheck,
  Plus,
  Landmark,
  Loader2,
  BadgeCheck,
  UploadCloud
} from 'lucide-react';
import OwnerTopNav from '../components/layout/OwnerTopNav';
import Sidebar from '../components/layout/Sidebar';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import RenterSettingsPanel from '../components/ui/RenterSettingsPanel';
import { ownerService } from '../services/owner.service';
import { setTokens, setUserProfile } from '../services/auth.service';
import { buildPublicFileUrl } from '../utils/fileUrl';

const defaultNotifications = {
  email: true,
  push: true,
  newBookings: true,
  payments: true,
  systemUpdates: false
};

const OwnerSettings = () => {
  const location = useLocation();
  const isRenter = location.pathname.startsWith('/renter/') || location.pathname.startsWith('/account/');
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState(null);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    identificationType: '',
    identificationNumber: ''
  });
  const [security, setSecurity] = useState({
    twoFactorEnabled: false,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [notifications, setNotifications] = useState(defaultNotifications);
  const [payout, setPayout] = useState({
    bankId: '',
    accountName: '',
    accountNumber: ''
  });
  const [verificationFiles, setVerificationFiles] = useState({
    identificationProof: null,
    partnershipContract: null
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        setError('');
        const [data, banks] = await Promise.all([
          ownerService.getSettings(),
          ownerService.getBanks()
        ]);
        applySettings({ ...data, banks });
      } catch (err) {
        setError(err.message || 'Failed to load settings.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const applySettings = (data) => {
    setSettings(data);
    setProfile({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      identificationType: data.identificationType || '',
      identificationNumber: data.identificationNumber || ''
    });
    setSecurity({
      twoFactorEnabled: Boolean(data.twoFactorEnabled),
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setNotifications({
      email: data.notifications?.email ?? true,
      push: data.notifications?.push ?? true,
      newBookings: data.notifications?.newBookings ?? true,
      payments: data.notifications?.payments ?? true,
      systemUpdates: data.notifications?.systemUpdates ?? false
    });
    setPayout({
      bankId: data.payout?.bankId || '',
      accountName: data.payout?.accountName || '',
      accountNumber: data.payout?.accountNumber || ''
    });
  };

  const handleProfileSave = async () => {
    try {
      setSavingSection('profile');
      setError('');
      setSuccess('');
      const updated = await ownerService.updateSettingsProfile(profile);
      applySettings(updated);
      if (updated.token || updated.refreshToken) {
        setTokens(updated.token, updated.refreshToken);
      }
      setUserProfile({
        firstName: updated.firstName,
        lastName: updated.lastName,
        email: updated.email,
        phone: updated.phone,
        identificationType: updated.identificationType,
        identificationNumber: updated.identificationNumber,
        customerType: updated.customerType || localStorage.getItem('userRole')
      });
      setSuccess('Profile information updated.');
    } catch (err) {
      setError(err.message || 'Failed to update profile.');
    } finally {
      setSavingSection('');
    }
  };

  const handleSecuritySave = async () => {
    try {
      setSavingSection('security');
      setError('');
      setSuccess('');
      const updated = await ownerService.updateSettingsSecurity(security);
      applySettings(updated);
      setSuccess('Security settings updated.');
    } catch (err) {
      setError(err.message || 'Failed to update security settings.');
    } finally {
      setSavingSection('');
    }
  };

  const handleNotificationsSave = async () => {
    try {
      setSavingSection('notifications');
      setError('');
      setSuccess('');
      const updated = await ownerService.updateSettingsNotifications(notifications);
      applySettings(updated);
      setSuccess('Notification settings updated.');
    } catch (err) {
      setError(err.message || 'Failed to update notifications.');
    } finally {
      setSavingSection('');
    }
  };

  const handlePayoutSave = async () => {
    try {
      setSavingSection('payout');
      setError('');
      setSuccess('');
      const updated = await ownerService.updateSettingsPayout(payout);
      applySettings(updated);
      setSuccess('Payout settings updated.');
    } catch (err) {
      setError(err.message || 'Failed to update payout settings.');
    } finally {
      setSavingSection('');
    }
  };

  const handleVerificationUpload = async (type) => {
    const file = verificationFiles[type];
    if (!file) {
      setError(type === 'identificationProof' ? 'Please choose an identification proof file.' : 'Please choose a partnership contract file.');
      return;
    }

    try {
      setSavingSection(type);
      setError('');
      setSuccess('');

      const updated = type === 'identificationProof'
        ? await ownerService.uploadIdentificationProof(file)
        : await ownerService.uploadPartnershipContract(file);

      applySettings({ ...settings, ...updated, banks: settings?.banks || [] });
      setUserProfile({
        customerType: updated.customerType,
        identificationType: updated.identificationType,
        identificationNumber: updated.identificationNumber,
        identificationProofUploaded: updated.identificationProofUploaded,
        partnershipContractUploaded: updated.partnershipContractUploaded,
        verificationComplete: updated.verificationComplete,
        requiresVerification: !updated.verificationComplete
      });
      setVerificationFiles((prev) => ({ ...prev, [type]: null }));
      setSuccess(type === 'identificationProof' ? 'Identification proof uploaded.' : 'Partnership contract uploaded.');
    } catch (err) {
      setError(err.message || 'Failed to upload document.');
    } finally {
      setSavingSection('');
    }
  };

  const toggleNotification = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fullName = `${profile.firstName} ${profile.lastName}`.trim() || 'Customer';
  const initials = `${profile.firstName[0] || ''}${profile.lastName[0] || ''}`.trim() || `${fullName[0] || 'C'}`;
  const selectedBank = settings?.banks?.find(bank => bank.id === payout.bankId);
  const pageDescription = isRenter
    ? 'Manage your Go Rides renter account, security, notifications, and identification details.'
    : 'Manage your Go Rides account, security, and preferences.';

  if (loading) {
    if (isRenter) {
      return (
        <div className="min-h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] flex flex-col">
          <Navbar />
          <main className="flex-grow flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden">
        <Sidebar activePage="Settings" />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </main>
      </div>
    );
  }

  if (isRenter) {
    return <RenterSettingsPanel {...{ settings, profile, setProfile, security, setSecurity, notifications, toggleNotification, verificationFiles, setVerificationFiles, savingSection, error, success, handleProfileSave, handleSecuritySave, handleNotificationsSave, handleVerificationUpload }} />;
  }

  return (
    <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden">
      <Sidebar activePage="Settings" />

      <main className="flex-grow overflow-y-auto">
        <div className="max-w-5xl mx-auto p-10 pb-32">
          <header className="mb-10">
            <h1 className="text-4xl font-black tracking-tight mb-2 uppercase">Settings</h1>
            <p className="text-gray-500 font-medium uppercase tracking-widest text-[10px] font-bold">{pageDescription}</p>
          </header>
          <OwnerTopNav activePage="Settings"/>

          {(error || success) && (
            <div className={`mb-8 rounded-2xl px-5 py-4 text-sm font-bold ${error ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-green-50 text-green-700 border border-green-100'}`}>
              {error || success}
            </div>
          )}

          <div className="space-y-8">
            <section className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-10 py-6 border-b border-gray-50 bg-gray-50/30">
                <h2 className="text-xs font-black uppercase tracking-widest">Profile Information</h2>
              </div>
              <div className="p-10">
                <div className="flex flex-col md:flex-row gap-12 items-start">
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-3xl bg-[#FDE7D1] flex items-center justify-center border-4 border-white shadow-lg shadow-[#FDE7D1]/20 overflow-hidden">
                      <span className="text-3xl font-black text-brand-black">{initials.toUpperCase()}</span>
                    </div>
                  </div>

                  <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">First Name</label>
                      <input
                        type="text"
                        value={profile.firstName}
                        onChange={(e) => setProfile(prev => ({ ...prev, firstName: e.target.value }))}
                        className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Last Name</label>
                      <input
                        type="text"
                        value={profile.lastName}
                        onChange={(e) => setProfile(prev => ({ ...prev, lastName: e.target.value }))}
                        className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Email Address</label>
                      <input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Phone Number</label>
                      <input
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Identification Type</label>
                      <select
                        value={profile.identificationType}
                        onChange={(e) => setProfile(prev => ({
                          ...prev,
                          identificationType: e.target.value,
                          identificationNumber: e.target.value === 'NATIONAL_ID'
                            ? prev.identificationNumber.replace(/\D/g, '').slice(0, 16)
                            : prev.identificationNumber
                        }))}
                        className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                      >
                        <option value="">Select identification type</option>
                        <option value="PASSPORT">Passport</option>
                        <option value="NATIONAL_ID">National ID</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Identification Number</label>
                      <input
                        type="text"
                        value={profile.identificationNumber}
                        onChange={(e) => setProfile(prev => ({
                          ...prev,
                          identificationNumber: prev.identificationType === 'NATIONAL_ID'
                            ? e.target.value.replace(/\D/g, '').slice(0, 16)
                            : e.target.value
                        }))}
                        placeholder={profile.identificationType === 'NATIONAL_ID' ? 'Enter 16-digit national ID' : 'Enter passport number'}
                        className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                      />
                      {profile.identificationType === 'NATIONAL_ID' && (
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Must contain exactly 16 digits</p>
                      )}
                    </div>
                    <div className="md:col-span-2 rounded-3xl border border-gray-100 bg-gray-50/60 p-6">
                      <div className="flex flex-col lg:flex-row gap-6 lg:items-start lg:justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-11 h-11 rounded-2xl bg-brand-black text-white flex items-center justify-center">
                              <BadgeCheck className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="text-sm font-black uppercase tracking-widest">Customer Identification</h3>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                {settings?.verificationComplete ? 'Verification Complete' : 'Verification Pending'}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 leading-6">
                            Keep your identification details up to date here. Uploaded documents stay available below for review and download.
                          </p>
                        </div>
                        <div className={`grid ${isRenter ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-3 min-w-[260px]`}>
                          <a
                            href={settings?.identificationProofPath ? buildPublicFileUrl(settings.identificationProofPath) : '#'}
                            target="_blank"
                            rel="noreferrer"
                            className={`inline-flex items-center justify-center rounded-2xl px-5 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${
                              settings?.identificationProofPath
                                ? 'bg-brand-black text-white hover:bg-black'
                                : 'bg-gray-100 text-gray-400 pointer-events-none'
                            }`}
                          >
                            Download ID Proof
                          </a>
                          {!isRenter && (
                            <a
                              href={settings?.partnershipContractPath ? buildPublicFileUrl(settings.partnershipContractPath) : '#'}
                              target="_blank"
                              rel="noreferrer"
                              className={`inline-flex items-center justify-center rounded-2xl px-5 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${
                                settings?.partnershipContractPath
                                  ? 'bg-brand-black text-white hover:bg-black'
                                  : 'bg-gray-100 text-gray-400 pointer-events-none'
                              }`}
                            >
                              Download Contract
                            </a>
                          )}
                        </div>
                      </div>

                      <div className={`grid grid-cols-1 ${isRenter ? '' : 'lg:grid-cols-2'} gap-5 mt-6`}>
                        <div className="rounded-2xl bg-white border border-gray-100 p-5">
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">Identification Proof</p>
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={(e) => setVerificationFiles((prev) => ({ ...prev, identificationProof: e.target.files?.[0] || null }))}
                            className="block w-full text-sm text-gray-600 file:mr-4 file:rounded-xl file:border-0 file:bg-brand-black file:px-4 file:py-2.5 file:text-sm file:font-bold file:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => handleVerificationUpload('identificationProof')}
                            disabled={savingSection === 'identificationProof'}
                            className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl border border-brand-black px-5 py-3 text-[10px] font-black uppercase tracking-widest text-brand-black disabled:opacity-50"
                          >
                            {savingSection === 'identificationProof' ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                            Upload ID Proof
                          </button>
                        </div>

                        {!isRenter && (
                          <div className="rounded-2xl bg-white border border-gray-100 p-5">
                            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">Partnership Contract</p>
                            <input
                              type="file"
                              accept=".pdf,.png,.jpg,.jpeg"
                              onChange={(e) => setVerificationFiles((prev) => ({ ...prev, partnershipContract: e.target.files?.[0] || null }))}
                              className="block w-full text-sm text-gray-600 file:mr-4 file:rounded-xl file:border-0 file:bg-brand-black file:px-4 file:py-2.5 file:text-sm file:font-bold file:text-white"
                            />
                            <button
                              type="button"
                              onClick={() => handleVerificationUpload('partnershipContract')}
                              disabled={savingSection === 'partnershipContract'}
                              className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl border border-brand-black px-5 py-3 text-[10px] font-black uppercase tracking-widest text-brand-black disabled:opacity-50"
                            >
                              {savingSection === 'partnershipContract' ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                              Upload Contract
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleProfileSave}
                        disabled={savingSection === 'profile'}
                        className="bg-brand-black text-white px-8 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-black/10 whitespace-nowrap disabled:opacity-50"
                      >
                        {savingSection === 'profile' ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-10 py-6 border-b border-gray-50 bg-gray-50/30">
                <h2 className="text-xs font-black uppercase tracking-widest">Account Security</h2>
              </div>
              <div className="p-10 space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-black mb-1">Two-Factor Authentication</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Use OTP verification during sign-in</p>
                  </div>
                  <button
                    onClick={() => setSecurity(prev => ({ ...prev, twoFactorEnabled: !prev.twoFactorEnabled }))}
                    className={`relative w-14 h-7 rounded-full transition-all duration-300 ${security.twoFactorEnabled ? 'bg-brand-black' : 'bg-gray-200'}`}
                  >
                    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all duration-300 shadow-sm ${security.twoFactorEnabled ? 'left-8' : 'left-1'}`}></div>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Current Password</label>
                    <input
                      type="password"
                      value={security.currentPassword}
                      onChange={(e) => setSecurity(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">New Password</label>
                    <input
                      type="password"
                      value={security.newPassword}
                      onChange={(e) => setSecurity(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Confirm Password</label>
                    <input
                      type="password"
                      value={security.confirmPassword}
                      onChange={(e) => setSecurity(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Password last updated: {settings?.passwordChangedAt || 'Never'}</p>
                  <button
                    onClick={handleSecuritySave}
                    disabled={savingSection === 'security'}
                    className="px-6 py-3 bg-brand-black text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all disabled:opacity-50"
                  >
                    {savingSection === 'security' ? 'Saving...' : 'Update Security'}
                  </button>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-10 py-6 border-b border-gray-50 bg-gray-50/30">
                <h2 className="text-xs font-black uppercase tracking-widest">Notification Settings</h2>
              </div>
              <div className="p-10">
                <div className="mb-10">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Communication Channels</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all cursor-pointer ${notifications.email ? 'border-brand-black bg-gray-100/70' : 'border-gray-50 bg-gray-50/10'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${notifications.email ? 'bg-brand-black text-white' : 'bg-gray-50 text-gray-400'}`}>
                          <Mail className="w-5 h-5" />
                        </div>
                        <span className="text-sm font-black">Email Notifications</span>
                      </div>
                      <input type="checkbox" className="hidden" checked={notifications.email} onChange={() => toggleNotification('email')} />
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${notifications.email ? 'bg-brand-black border-brand-black' : 'border-gray-200 bg-white'}`}>
                        {notifications.email && <ShieldCheck className="w-4 h-4 text-white" />}
                      </div>
                    </label>

                    <label className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all cursor-pointer ${notifications.push ? 'border-brand-black bg-gray-100/70' : 'border-gray-50 bg-gray-50/10'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${notifications.push ? 'bg-brand-black text-white' : 'bg-gray-50 text-gray-400'}`}>
                          <Bell className="w-5 h-5" />
                        </div>
                        <span className="text-sm font-black">Push Notifications</span>
                      </div>
                      <input type="checkbox" className="hidden" checked={notifications.push} onChange={() => toggleNotification('push')} />
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${notifications.push ? 'bg-brand-black border-brand-black' : 'border-gray-200 bg-white'}`}>
                        {notifications.push && <ShieldCheck className="w-4 h-4 text-white" />}
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Alert Types</p>
                  <div className="space-y-4">
                    {[
                      { id: 'newBookings', title: 'New Bookings', desc: 'Get notified when someone requests a ride' },
                      { id: 'payments', title: 'Payments & Earnings', desc: 'Notifications for successful payouts and balance updates' },
                      { id: 'systemUpdates', title: 'System Updates', desc: 'News about app updates and platform changes' }
                    ].map((type) => (
                      <label key={type.id} className="flex items-start gap-4 cursor-pointer group">
                        <div className="pt-0.5">
                          <input type="checkbox" className="hidden" checked={notifications[type.id]} onChange={() => toggleNotification(type.id)} />
                          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${notifications[type.id] ? 'bg-brand-black border-brand-black shadow-md shadow-black/10' : 'border-gray-200 bg-white group-hover:border-gray-300'}`}>
                            {notifications[type.id] && <div className="w-2.5 h-1.5 border-b-2 border-l-2 border-white -rotate-45 mb-1"></div>}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-black">{type.title}</p>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{type.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    onClick={handleNotificationsSave}
                    disabled={savingSection === 'notifications'}
                    className="bg-brand-black text-white px-8 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-black/10 disabled:opacity-50"
                  >
                    {savingSection === 'notifications' ? 'Saving...' : 'Save Notifications'}
                  </button>
                </div>
              </div>
            </section>

            {!isRenter && (
              <section className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-10 py-6 border-b border-gray-50 bg-gray-50/30">
                  <h2 className="text-xs font-black uppercase tracking-widest">Payout Settings</h2>
                </div>
                <div className="p-10">
                  <div className="bg-brand-black rounded-[2.5rem] p-8 text-white flex items-center justify-between mb-8 shadow-2xl shadow-black/10 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 group-hover:scale-110 transition-transform duration-700"></div>
                    <div className="flex items-center gap-6 relative z-10">
                      <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-brand-black shadow-xl">
                        <Landmark className="w-7 h-7" />
                      </div>
                      <div>
                        <h3 className="text-xl font-black mb-1">{selectedBank?.name || settings?.payout?.bankName || 'No bank selected'}</h3>
                        <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest opacity-80">
                          {payout.accountNumber ? `•••• ${payout.accountNumber.slice(-4)}` : 'No account'} | {payout.accountName || fullName}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-gray-100 overflow-hidden">
                    <div className="divide-y divide-gray-100">
                      <div className="grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)] items-center gap-4 p-6">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Bank</p>
                        </div>
                        <select
                          value={payout.bankId}
                          onChange={(e) => setPayout(prev => ({ ...prev, bankId: e.target.value }))}
                          className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                        >
                          <option value="">Select bank</option>
                          {settings?.banks?.map(bank => (
                            <option key={bank.id} value={bank.id}>{bank.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)] items-center gap-4 p-6">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Account Name</p>
                        </div>
                        <input
                          type="text"
                          value={payout.accountName}
                          onChange={(e) => setPayout(prev => ({ ...prev, accountName: e.target.value }))}
                          className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)] items-center gap-4 p-6">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Account Number</p>
                        </div>
                        <input
                          type="text"
                          value={payout.accountNumber}
                          onChange={(e) => setPayout(prev => ({ ...prev, accountNumber: e.target.value }))}
                          className="w-full px-5 py-3.5 bg-gray-50 border-transparent rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col items-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6">Your earnings are automatically transferred to this account every Monday.</p>
                    <button
                      onClick={handlePayoutSave}
                      disabled={savingSection === 'payout'}
                      className="flex items-center gap-2 px-8 py-3.5 bg-white border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                      {savingSection === 'payout' ? 'Saving...' : 'Save Payout Settings'}
                    </button>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default OwnerSettings;
